import type { UserSummaryDTO } from "@zplanem/responses"
import { type ChatRole, chatRoleBorderClass } from "../../lib/role-colors"
import { getInitials } from "../../lib/user-display"
import { cn } from "../../lib/utils"
import { MareczekAvatar } from "./mareczek-avatar"

interface Props {
	user: UserSummaryDTO | null
	chatRole: ChatRole
	/** Pre-composed hover label, e.g. "Patryk Wojtynski (Owner)". */
	title: string
	size?: number
	className?: string
}

/**
 * Small circular avatar for chat author identity.
 *
 * - **Non-AI roles** (`owner`, `member`, `contractor`, `architect`): render a
 *   bordered transparent circle with the user's initials in white. The border
 *   color encodes the role per `chatRoleBorderClass`.
 * - **AI role** (`ai`): defer to the existing `MareczekAvatar` (purple
 *   gradient + AI badge), wrapped so the hover `title` lives on a span with
 *   an explicit `role="img"`.
 */
export function AuthorAvatar({
	user,
	chatRole,
	title,
	size = 28,
	className,
}: Props) {
	if (chatRole === "ai") {
		const badgeFontSize = Math.max(7, Math.round(size * 0.25))
		return (
			<span
				role="img"
				title={title}
				aria-label={title}
				className={cn("inline-flex shrink-0", className)}
			>
				<MareczekAvatar size={size} badgeFontSize={badgeFontSize} />
			</span>
		)
	}
	const initials = getInitials(user)
	const fontSize = Math.round(size * 0.4)
	const avatarUrl = user?.avatarUrl?.trim()
	return (
		<span
			role="img"
			title={title}
			aria-label={title}
			className={cn(
				"shrink-0 inline-flex items-center justify-center rounded-full bg-transparent text-foreground font-bold border-2 select-none",
				avatarUrl && "overflow-hidden",
				chatRoleBorderClass(chatRole),
				className,
			)}
			style={{ width: size, height: size, fontSize }}
		>
			{avatarUrl ? (
				<img
					src={avatarUrl}
					alt=""
					aria-hidden="true"
					className="h-full w-full rounded-full object-cover"
				/>
			) : (
				initials
			)}
		</span>
	)
}
